import { showTransientNotification } from '@driftt/traffic-control'
import { path, pathOr } from 'ramda'
import * as R from 'ramda'
import React, { useCallback, useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { connect, useSelector } from 'react-redux'
import { Redirect, withRouter } from 'react-router-dom'

import { FetchFLSettingsQuery } from 'api/fastlane'
import { getClassifications, getDraft, getPlaybook } from 'api/playbooks'
import Loader from 'components/UniversalLoader'
import BotEditorLayout from 'containers/BotEditorLayout'
import { useConnectedCalendarIds } from 'hooks/useConnectedCalendarIds'
import { useTeams } from 'hooks/useTeams'
import { receiveClassifications } from 'modules/classifications/actions'
import { setBotFlowMode, setPlaybookType } from 'modules/editor/actions'
import { BOT_FLOW_MODES } from 'modules/editor/reducer'
import { receivePlaybook } from 'modules/playbook/actions'
import { validatePlaybook } from 'modules/playbookValidation/actions'
import { pause } from 'utils'
import {
  hasPlaybookContentViewMode,
  userHasChatPlaybooksWrite,
} from 'utils/access'
import {
  getPlaybookCampaignId,
  isFastLanePlaybook,
  isMeetingBookingPlaybook,
} from 'utils/playbooks'
import { trackPlaybookPublish } from 'utils/tracking'

const EditorLayoutManager = (props) => {
  const {
    match,
    showTransientNotification,
    onReceivePlaybook,
    onReceiveClassifications,
    onValidatePlaybook,
    resetBotFlowMode,
    setViewOnlyBotFlowMode,
  } = props

  const [hasFailed, setHasFailed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [playbook, setPlaybook] = useState({})
  const [publishedPlaybook, setPublishedPlaybook] = useState({})
  const [editStartTime, setEditStartTime] = useState(new Date())
  const connectedCalendarIds = useConnectedCalendarIds()
  const orgUsers = useSelector(R.pathOr([], ['org', 'users']))
  const orgTeams = useTeams()
  const blocksById = useSelector(
    path(['__BOT_EDITOR_v2', 'blocks', 'blocksById'])
  )

  const campaignId = getPlaybookCampaignId(playbook)
  const isFastlane = isFastLanePlaybook(playbook)
  const isMeetingBooking = isMeetingBookingPlaybook(playbook)
  const playbookReportType = pathOr(null, ['reportType'], playbook)

  // If the playbook is fastlane, fetch fastlane settings so we can determine if is quick setup
  const { data: fastlaneSettingsData, isLoading: isFLLoading } = useQuery({
    queryKey: FetchFLSettingsQuery.queryKey(campaignId),
    queryFn: () => FetchFLSettingsQuery.queryFn(campaignId),
    enabled: isFastlane,
  })

  const isFastlaneQuickSetup =
    fastlaneSettingsData?.fastlaneSettings.fastlaneSettingsType ===
    'QUICK_SETUP'

  const trackPublishAndResetTimer = useCallback(() => {
    const publishTime = new Date()
    const seconds = Math.abs(publishTime - editStartTime) / 1000
    trackPlaybookPublish(seconds)
    setEditStartTime(publishTime)
  }, [editStartTime])

  const receivePlaybookData = useCallback(
    async ([
      { data: playbookData },
      { data: classificationsData },
      { data: publishedPlaybookData },
    ]) => {
      onReceivePlaybook(playbookData)
      onValidatePlaybook(
        playbookData,
        blocksById,
        connectedCalendarIds,
        orgTeams,
        orgUsers
      )
      onReceiveClassifications(classificationsData)

      await pause(3500)

      setPlaybook(playbookData)
      setPublishedPlaybook(publishedPlaybookData)
      setIsLoading(false)
    },
    [
      onReceivePlaybook,
      onValidatePlaybook,
      blocksById,
      connectedCalendarIds,
      orgTeams,
      orgUsers,
      onReceiveClassifications,
    ]
  )

  useEffect(() => {
    async function fetchPlaybook() {
      try {
        await Promise.all([
          getDraft(match.params.id),
          getClassifications(),
          getPlaybook(match.params.id),
        ]).then(receivePlaybookData)
      } catch (err) {
        console.warn(err)
        showTransientNotification({
          type: 'error',
          message: 'We could not find your playbook.',
        })

        setHasFailed(true)
      }
    }

    fetchPlaybook()
  }, [match, showTransientNotification, receivePlaybookData])

  useEffect(() => {
    if (hasPlaybookContentViewMode()) {
      if (userHasChatPlaybooksWrite()) {
        resetBotFlowMode()
      } else {
        setViewOnlyBotFlowMode()
      }
    }
  }, [match.params.id, resetBotFlowMode, setViewOnlyBotFlowMode])

  const isPlaybookLoading = isLoading || isFLLoading

  if (isPlaybookLoading) {
    return (
      <Loader
        className="playbook-editor-loading"
        loaderText="Loading your playbook data…"
      />
    )
  }

  if (hasFailed) {
    return <Redirect to="/not-found" />
  }

  const passThroughProps = {
    ...props,
    playbook,
    publishedPlaybook,
    trackPublishAndResetTimer,
    blocksById,
    isFastlane,
    isFastlaneQuickSetup,
    isMeetingBooking,
    playbookReportType,
  }

  return <BotEditorLayout {...passThroughProps} />
}

const mapStateToProps = (state) => ({
  outdatedBlocks: pathOr([], ['__BOT_EDITOR_v2', 'outdatedBlocks'])(state),
})

const mapDispatchToProps = (dispatch) => ({
  onReceivePlaybook: (playbook) => {
    dispatch(receivePlaybook(playbook))
    dispatch(setPlaybookType(playbook.reportType))
  },
  onReceiveClassifications: (classifications) => {
    dispatch(receiveClassifications(classifications))
  },
  onValidatePlaybook: (
    playbook,
    blocksById,
    connectedCalendarIds,
    orgTeams,
    orgUsers
  ) => {
    dispatch(
      validatePlaybook(
        playbook,
        blocksById,
        connectedCalendarIds,
        orgTeams,
        orgUsers
      )
    )
  },
  showTransientNotification: (config) => {
    dispatch(showTransientNotification(config))
  },
  resetBotFlowMode: () => {
    dispatch(setBotFlowMode(BOT_FLOW_MODES.BUILD))
  },
  setViewOnlyBotFlowMode: () => {
    dispatch(setBotFlowMode(BOT_FLOW_MODES.CONTENT_VIEW))
  },
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EditorLayoutManager)
)
